import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config';

const pagingAndSortingSchema = z.object({
	paging: z.object({
		pageSize: z.number(),
		pageNumber: z.number(),
	}),
	sorting: z.object({
		columnName: z.string(),
		sortOrder: z.union([z.literal(1), z.literal(2)]),
	}),
});

export const requestSchema = z
	.object({
		acquirerID: z.string().optional(),
		cardTypeId: z.number().optional(),
		customerInvoiceNo: z.string().optional(),
		customerName: z.string().optional(),
		icInvoiceNo: z.string().optional(),
		insuranceCompanyId: z.number().optional(),
		isSettled: z.boolean().optional(),
		passportNumber: z.string().optional(),
		paymentDateFrom: z.string().optional(),
		paymentDateTo: z.string().optional(),
		paymentGatewayReferenceNo: z.string().optional(),
		paymentMethodId: z.number().optional(),
		paymentPricingTypeId: z.number().optional(),
		planId: z.number().optional(),
		policyEffectiveDateFrom: z.string().optional(),
		policyEffectiveDateTo: z.string().optional(),
		policyExpiryDateFrom: z.string().optional(),
		policyExpiryDateTo: z.string().optional(),
		policyIssueDateFrom: z.string().optional(),
		policyIssueDateTo: z.string().optional(),
		policyNumber: z.string().optional(),
		policyStatusId: z.number().optional(),
		policyTypeId: z.number().optional(),
		recordIds: z.array(z.string()).optional(),
		shoryTransactionNo: z.string().optional(),
		sourceId: z.number().optional(),
		sponsorEID: z.string().optional(),
		sponsorName: z.string().optional(),
		quoteReferenceNumbers: z.array(z.string()).optional(),
		planTypeId: z.number().optional(),
		planEmirateId: z.number().optional(),
		isDiscountApplied: z.boolean().optional(),
	})
	.merge(pagingAndSortingSchema);

const responseRecordSchema = z.object({
	id: z.string(),
	policyId: z.string(),
	customerId: z.string(),
	policyNumber: z.string(),
	customerMobile: z.string(),
	customerInvoiceNumber: z.string(),
	customerInvoiceUrl: z.string(),
	customerName: z.string().nullish(),
	customerEmail: z.string(),
	insuranceCompanyName: z.string(),
	policyType: z.string(),
	planName: z.string(),
	policyPremiumExcludingVAT: z.number(),
	policyPremiumIncludingVAT: z.number(),
	vatOnPolicyPremium: z.number(),
	basmahFees: z.number(),
	vatOnBasmahFees: z.number(),
	icpFeesExclVAT: z.number(),
	vatAmountOnICPFees: z.number(),
	dohFine: z.number(),
	effectiveDate: z.string(),
	expiryDate: z.string(),
	issueDate: z.string(),
	paymentDate: z.string(),
	paymentMethod: z.string(),
	paymentPricingType: z.string(),
	paymentGatewayFees: z.number(),
	shoryTransactionNo: z.string(),
	paymentGatewayReferenceNo: z.string(),
	acquirerID: z.string(),
	dueToIC: z.number(),
	duetoShory: z.number(),
	quoteReferenceNumber: z.string(),
	policyStatus: z.string(),
	settlementDate: z.string().nullish(),
	settlementReferenceNo: z.string().nullish(),
	commissionAmount: z.number(),
	vatOnCommission: z.number(),
	cardType: z.string(),
	grossPremium: z.number(),
	icInvoiceNumber: z.string().nullish(),
	icInvoiceUrl: z.string().nullish(),
	sponsorName: z.string().optional(),
	icAdditionalFeesExcludingVAT: z.number(),
	vatOnICAdditionalFees: z.number(),
	planType: z.string(),
	planEmirate: z.string(),
	bmiLoading: z.number().nullish(),
	vatOnBMILoading: z.number().nullish(),
	cancellationDetails: z.string(),
	isDiscountApplied: z.boolean().nullish(),
	typeOfDiscount: z.string().nullish(),
	discountRate: z.string().nullish(),
	discountExcludingVAT: z.number().nullish(),
	vaTonDiscount: z.number().nullish(),
	discountIncludingVAT: z.number().nullish(),
	actualAmountPaidIncludingVAT: z.number().nullish(),
	vatPercentage: z.number().nullish(),
});

const responseSchema = z.object({
	data: z.array(responseRecordSchema),
	totalItemsCount: z.number(),
});

const postInsuranceCompaniesReconciliationReport = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/report/ic-recon',
		method: 'POST',
		responseSchema,
		requestSchema,
		transformResponse: (response) => ({
			data: response.data.report,
			totalItemsCount: response.data.paging.totalRecords,
		}),
	});

export type PostInsuranceCompaniesReconciliationReportRequest = z.infer<typeof requestSchema>;

export type PostInsuranceCompaniesReconciliationReportResponse = TypeOfAPIResponse<
	ReturnType<typeof postInsuranceCompaniesReconciliationReport>
>;

export type PostInsuranceCompaniesReconciliationReportRecord = z.infer<typeof responseRecordSchema>;

export type PostInsuranceCompaniesReconciliationReportError = TypeOfAPIError<
	ReturnType<typeof postInsuranceCompaniesReconciliationReport>
>;

export default postInsuranceCompaniesReconciliationReport;
